import {
  addToast,
  Button,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useForm } from "@mantine/form";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import { LimeSwitch } from "@/Components/NextBase/LimeSwitch";
import { api } from "@/lib/api-client";
import { getApiErrorMessage } from "@/utils/api-error";

type ReactivateFormValues = {
  restoreCanceledAppointments: boolean;
};

export function CustomerSubscriptionReactivateModal({
  isOpen,
  onClose,
  customerSubscriptionId,
}: {
  isOpen: boolean;
  onClose: () => void;
  customerSubscriptionId: number;
}) {
  const form = useForm<ReactivateFormValues>({
    initialValues: { restoreCanceledAppointments: true },
  });

  const { mutateAsync: updateCustomerSubscription, isPending } =
    api.customer.useUpdateCustomerSubscription();

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleReactivate = async () => {
    try {
      await updateCustomerSubscription({
        customerSubscriptionId,
        body: {
          status: "active",
          restoreCanceledAppointments: form.values.restoreCanceledAppointments,
        },
      });
      addToast({
        title: t`Paket ponovno aktiviran`,
        description: t`Paket je bil uspešno ponovno aktiviran`,
        color: "success",
      });
      handleClose();
    } catch (error) {
      addToast({
        title: t`Napaka`,
        description:
          getApiErrorMessage(error) ?? t`Ponovna aktivacija paketa ni uspela`,
        color: "danger",
      });
    }
  };

  return (
    <LimeModal
      isOpen={isOpen}
      onClose={handleClose}
      data-identifier="reactivate-subscription-modal"
    >
      <ModalContent>
        <ModalHeader>
          <Trans>Ponovna aktivacija paketa</Trans>
        </ModalHeader>
        <ModalBody>
          <LimeSwitch
            size="sm"
            isCompact
            {...form.getInputProps("restoreCanceledAppointments", {
              type: "checkbox",
            })}
          >
            <Trans>
              Povrni termine, ki so bili odpovedani ob preklicu tega paketa.
            </Trans>
          </LimeSwitch>
          <p className="text-default-400 text-xs">
            <Trans>Povrnjeni bodo samo termini, ki se še niso začeli.</Trans>
          </p>
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={handleClose} isDisabled={isPending}>
            <Trans>Prekliči</Trans>
          </Button>
          <Button
            data-identifier="reactivate-subscription-confirm-btn"
            color="primary"
            variant="solid"
            onPress={handleReactivate}
            isLoading={isPending}
          >
            <Trans>Ponovno aktiviraj</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </LimeModal>
  );
}
